<template>
  <div class="bg-white w-100pc h-100pc">
    <v-container
      v-if="loaded"
      class="mxw-800 mx-auto py-12"
    >
      <div
        ref="progressBar"
        :aria-label="$bct('Progress indicator - question') + ' ' + stepCount"
        class="px-4 focus-invisible"
        role="progressbar"
        tabindex="0"
        aria-live
      >
        <v-progress-linear
          v-model="progress"
          :indeterminate="!loaded"
          class="focus-invisible mb-4"
          color="primary"
        />
      </div>

      <div class="px-4">
        <template v-if="syncedSchema?.data_type == 'Provider'">
          <QuestionSet
            v-model="provider"
            @back="backFromQuestion($event)"
            @next="forwardFromQuestion($event)"
            :processing="processing"
            :questions="validQuestions"
            :readonly="form.locked"
            :schema="syncedSchema.definition"
            :section="section"
            :transition-name="transitionName"
            key-name="question"
          />
        </template>

        <template v-else>
          <QuestionSet
            v-model="form"
            @back="backFromQuestion($event)"
            @change:attachments="loadAttachments"
            @next="forwardFromQuestion($event)"
            :attachment-owner-id="form.id"
            :attachment-owner-type="'Form'"
            :attachments="attachments"
            :processing="processing"
            :questions="validQuestions"
            :readonly="form.locked"
            :schema="syncedSchema.definition"
            :section="section"
            :transition-name="transitionName"
            key-name="question"
          />
        </template>
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useQuestionable from '@/shared/composables/useQuestionable';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useStepper from '@/shared/composables/useStepper';

const $bct = inject('translator');

const { api: Api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const router = useRouter();
const { updateQuery } = useRouterHelper();
const store = useStore();

const attachments = ref([]);
const form = ref(null);
const formSchema = ref(null);
const processing = ref(false);
const progressBar = ref(null);
const provider = ref(null);
const providerSchema = ref(null);

const workflowTaskId = computed(() => route.params?.workflowTaskId ?? null);
const workflowTaskMode = computed(() => !!workflowTaskId.value);

const syncedSchema = computed(() => {
  if (questions.value[0]?.synced_model === 'Provider') return providerSchema.value;
  return formSchema.value;
});
const formSchemaId = computed(() => {
  if (workflowTaskMode.value) return form.value?.schema_id ?? null;
  return route.params?.schemaId ?? null;
});

const { questions, loadQuestions, questionsLoaded, validateAnswers, validQuestions } =
  useQuestionable({
    ownerDataTypeRef: ref('Schema'),
    ownerIdRef: formSchemaId,
    syncedObjectRef: form,
  });

const { progress, section, stepBack, stepCount, stepForward, stepTotal, transitionName } =
  useStepper({
    processing,
    route,
    updateQuery,
  });

const loaded = computed(() => {
  if (!form.value || !formSchema.value || !questionsLoaded.value || questions.value.length === 0) {
    return false;
  }
  if (workflowTaskMode.value) return true;
  return !!provider.value;
});

const exitDestination = computed(() => {
  if (workflowTaskMode.value) {
    const workflowInstanceId =
      route.query.returnToWorkflowInstanceId || form.value?.workflow_instance_id;
    if (workflowInstanceId) {
      return { name: 'WorkflowInstanceShow', params: { workflowInstanceId } };
    }
    return { name: 'Dashboard' };
  }
  return { name: 'ProviderIndex' };
});

watch(
  () => loaded.value,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      if (route.query.step) stepCount.value = parseInt(route.query.step, 10);
      else stepCount.value = 0;

      if (!stepTotal) await validateAnswers();

      recalculateStepTotal();

      if (route.query.section) {
        section.value = route.query.section;
        progressBar.value?.focus();
        progress.value = (stepCount.value / stepTotal) * 100;
      } else {
        start();
      }
    }
  },
  { immediate: true },
);

watch(
  () => questionsLoaded.value,
  (newVal) => {
    if (newVal) recalculateStepTotal();
  },
);

watch(
  () => route.query.section,
  async (newVal) => {
    section.value = null;
    stepCount.value = parseInt(route.query.step, 10);

    if (!stepTotal.value) await validateAnswers();

    recalculateStepTotal();
    if (newVal) {
      section.value = newVal;
      progressBar.value?.focus();
    } else {
      section.value = 'question-0';
    }
    progress.value = (stepCount.value / stepTotal.value) * 100;
  },
);

onMounted(load);

async function load() {
  if (workflowTaskMode.value) {
    await loadWorkflowTaskForm();
    formSchema.value = store.state.schemas[form.value?.schema_id];
  } else {
    formSchema.value = store.state.schemas[route.params.schemaId];
    loadProvider();
    await loadForm();
  }
  void loadAttachments();
  await loadQuestions();
}

async function loadWorkflowTaskForm() {
  const response = await Api.manager.workflowTaskForm.get(workflowTaskId.value);
  form.value = response?.data ?? null;
}

async function loadForm() {
  const filters = {
    schema_id: route.params.schemaId,
  };

  const { data } = await Api.manager.provider.form.index(route.params.providerId, filters);
  [form.value] = data;

  if (data && !form.value) {
    const response = await Api.manager.provider.form.create(route.params.providerId, {
      schema_id: route.params.schemaId,
    });
    form.value = response.data;
  }
}

async function loadAttachments() {
  const params = {
    owner_type: 'Form',
    owner_id: form.value.id,
  };
  const response = await Api.member.attachment.index(params);
  if (response?.status !== 200) return;
  attachments.value = response.data;
}

function loadProvider() {
  Api.manager.provider.get(route.params.providerId, (response) => {
    provider.value = response.data;
    providerSchema.value = store.state.schemas[provider.value.schema_id];
  });
}

async function maybeSaveProvider() {
  if (workflowTaskMode.value) return;
  if (syncedSchema.value.data_type !== 'Provider') return;
  await Api.provider.update(route.params.providerId, provider.value);
}

async function saveForm() {
  if (workflowTaskMode.value) {
    try {
      const response = await workflowTaskFormUpdate({
        custom: form.value.custom,
      });
      if (response?.data?.custom !== undefined) {
        form.value.custom = response.data.custom;
      }
    } catch (error) {
      throw new Error(errorMessageFor(error, 'Could not save form progress'));
    }
    return;
  }

  const { data } = await Api.manager.provider.form.update(
    route.params.providerId,
    form.value.id,
    form.value,
  );
  form.value = data;
}

async function submitForm() {
  if (workflowTaskMode.value) {
    let response;
    try {
      response = await workflowTaskFormUpdate({
        custom: form.value.custom,
        submitted: true,
      });
    } catch (error) {
      throw new Error(errorMessageFor(error, 'Form submission failed'));
    }

    if (!response?.data) {
      throw new Error('Form submission failed');
    }

    form.value = response.data;
    return;
  }

  form.value.submitted = true;
  const response = await Api.manager.provider.form.update(
    route.params.providerId,
    form.value.id,
    form.value,
  );

  if (response?.status >= 400) {
    throw new Error(response?.data?.errors?.[0] ?? 'Form submission failed');
  }

  if (response?.data) form.value = response.data;
}

function errorMessageFor(error, fallback) {
  return error?.response?.data?.errors?.[0] ?? error?.message ?? fallback;
}

function workflowTaskFormUpdate(formPayload) {
  return Api.manager.workflowTaskForm.update(workflowTaskId.value, formPayload, {}, null, (error) =>
    Promise.reject(error),
  );
}

async function backFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    try {
      await saveForm();
    } catch (error) {
      processing.value = false;
      eventBus.chime(error?.message || 'Could not save form progress');
      return;
    }
    await maybeSaveProvider();
    await validateAnswers();
  }
  recalculateStepTotal();

  if (index - 1 < 0) {
    await router.push(exitDestination.value);
  } else {
    stepBack(`question-${index - 1}`);
  }
}

async function finish() {
  processing.value = true;
  if (!form.value.locked) {
    try {
      await submitForm();
    } catch (error) {
      processing.value = false;
      eventBus.chime(error?.message || 'Form submission failed');
      return;
    }
    eventBus.chime('Form submitted');
  }

  if (formSchema.value?.meta?.submitted_redirect_url) {
    window.location.replace(formSchema.value.meta.submitted_redirect_url);
    return;
  }
  await router.push(exitDestination.value);
}

async function forwardFromQuestion(index) {
  processing.value = true;
  if (!form.value.locked) {
    try {
      await saveForm();
    } catch (error) {
      processing.value = false;
      eventBus.chime(error?.message || 'Could not save form progress');
      return;
    }
    await maybeSaveProvider();
    await validateAnswers();
  }
  recalculateStepTotal();

  if (index + 1 >= validQuestions.value.length) {
    await finish();
  } else {
    stepForward(`question-${index + 1}`);
  }
}

function recalculateStepTotal() {
  stepTotal.value = validQuestions.value.length;
  progress.value = (stepCount.value / stepTotal.value) * 100;
}

function start() {
  processing.value = true;
  if (validQuestions.value.length > 0) {
    stepForward('question-0');
  } else {
    stepForward('finish');
  }
}
</script>
